import React from "react";
import { Check, EyeOff } from "lucide-react";
import { Reveal } from "../hooks/useReveal";
import { pricingTiers, TRUST_PORTAL_URL } from "../data/siteContent";

const Pricing = () => {
  return (
    <section className="bg-parchment py-24 sm:py-36" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-3xl">
          <div className="flex items-center gap-3 text-[11px] sm:text-xs tracking-[0.3em] uppercase font-semibold text-gold">
            <span className="h-px w-10 bg-gold/60" />
            Pricing
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ink tracking-tight leading-[1.06] mt-6">
            Good Trust or Great Trust. <em className="text-goldeep italic">There Is a Difference.</em>
          </h2>
          <p className="text-ink/70 text-base sm:text-lg leading-relaxed mt-6">
            Most people do not know this but not all trusts are created
            equal. Here is what separates a trust that protects you from a
            trust that makes you completely invisible.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mt-14 sm:mt-20 max-w-5xl mx-auto">
          {pricingTiers.map((tier, idx) => (
            <Reveal key={tier.name} className="h-full" delay={idx * 80}>
              {tier.recommended ? (
                <div
                  className="relative h-full bg-ink text-cream rounded-3xl p-8 sm:p-12 shadow-2xl border border-gold/40 hover:-translate-y-1.5 transition-transform duration-500 flex flex-col overflow-hidden"
                  data-testid="pricing-private-card"
                >
                  <div className="absolute -top-20 -right-20 w-60 h-60 bg-gold/15 blur-3xl rounded-full" aria-hidden="true" />
                  <span className="absolute top-6 right-6 bg-gold text-ink text-[10px] font-bold tracking-[0.2em] uppercase px-4 py-1.5 rounded-full">
                    Recommended
                  </span>
                  <p className="text-xs tracking-[0.28em] uppercase font-semibold text-gold">{tier.name}</p>
                  <p className="font-display text-4xl sm:text-5xl text-cream mt-5">
                    {tier.price}
                    <span className="text-lg text-cream/40">.</span>
                  </p>
                  <div className="h-px bg-gold/25 my-8" />
                  <p className="text-cream/75 leading-relaxed flex-1">{tier.text}</p>
                  <a
                    href={TRUST_PORTAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pricing-private-btn"
                    className="mt-10 inline-flex justify-center items-center gap-2 bg-gold text-ink font-bold text-sm tracking-[0.1em] uppercase px-8 py-4 rounded-full hover:bg-goldlight transition-colors duration-300 shadow-[0_0_35px_rgba(201,162,60,0.35)]"
                  >
                    <EyeOff className="w-4 h-4" /> Get Started
                  </a>
                </div>
              ) : (
                <div
                  className="h-full bg-white border border-ink/10 rounded-3xl p-8 sm:p-12 shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-[box-shadow,transform] duration-500 flex flex-col"
                  data-testid="pricing-basic-card"
                >
                  <p className="text-xs tracking-[0.28em] uppercase font-semibold text-ink/50">{tier.name}</p>
                  <p className="font-display text-4xl sm:text-5xl text-ink mt-5">
                    {tier.price}
                    <span className="text-lg text-ink/40">.</span>
                  </p>
                  <div className="h-px bg-ink/10 my-8" />
                  <p className="text-ink/70 leading-relaxed flex-1">{tier.text}</p>
                  <a
                    href={TRUST_PORTAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pricing-basic-btn"
                    className="mt-10 inline-flex justify-center items-center gap-2 border-2 border-ink text-ink font-bold text-sm tracking-[0.1em] uppercase px-8 py-4 rounded-full hover:bg-ink hover:text-cream transition-colors duration-300"
                  >
                    <Check className="w-4 h-4" /> Get Started
                  </a>
                </div>
              )}
            </Reveal>
          ))}
        </div>

        <Reveal className="text-center mt-12 max-w-2xl mx-auto">
          <p className="text-ink/80 font-medium text-base sm:text-lg">
            Start at $1,497 now. Upgrade to private for $1,000 more when you
            are ready.
          </p>
          <p className="font-display italic text-goldeep text-base sm:text-lg mt-3">
            Both protect you. Only one makes you invisible. Ask about going
            private when we speak.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;
